import React, { useState, useEffect } from 'react';
import { 
  UserPlus, 
  Search, 
  X, 
  Users,
  CheckCircle,
  AlertCircle,
  Loader
} from 'lucide-react';
import { db } from '../../firebase/config';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  serverTimestamp,
  doc,
  setDoc
} from 'firebase/firestore';
import toast from 'react-hot-toast';

const BatchStudentEnrollment = ({ batchId, coachingId, onClose, onEnrollmentComplete }) => {
  const [students, setStudents] = useState([]);
  const [enrolledStudents, setEnrolledStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudents, setSelectedStudents] = useState([]);

  useEffect(() => {
    loadData();
  }, [batchId, coachingId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load all students from student management
      const studentsRef = collection(db, 'students');
      const studentsQuery = query(studentsRef, where('coachingId', '==', coachingId));
      const studentsSnapshot = await getDocs(studentsQuery);
      
      const studentsData = studentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setStudents(studentsData);
      
      // Load already enrolled students
      const enrollmentsRef = collection(db, 'batchEnrollments');
      const enrollmentsQuery = query(enrollmentsRef, where('batchId', '==', batchId));
      const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
      
      const enrolledEmails = enrollmentsSnapshot.docs.map(doc => doc.data().email?.toLowerCase());
      setEnrolledStudents(enrolledEmails);
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const handleEnrollStudents = async () => {
    if (selectedStudents.length === 0) {
      toast.error('Please select at least one student');
      return;
    }

    try {
      setEnrolling(true);
      let successCount = 0;
      let errorCount = 0;

      for (const studentId of selectedStudents) {
        const student = students.find(s => s.id === studentId);
        if (!student) continue;

        try {
          // Check if already enrolled
          if (enrolledStudents.includes(student.email?.toLowerCase())) {
            errorCount++;
            continue;
          }

          // Create enrollment
          const enrollmentRef = doc(collection(db, 'batchEnrollments'));
          await setDoc(enrollmentRef, {
            batchId,
            studentId: student.id,
            email: student.email?.toLowerCase(),
            name: student.name,
            phone: student.phone,
            coachingId,
            status: 'active',
            enrolledAt: serverTimestamp(),
            createdAt: serverTimestamp()
          });

          successCount++;
        } catch (error) {
          console.error(`Error enrolling student ${student.name}:`, error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully enrolled ${successCount} student(s)`);
      }
      if (errorCount > 0) {
        toast.error(`Failed to enroll ${errorCount} student(s)`);
      }

      if (onEnrollmentComplete) {
        onEnrollmentComplete();
      }
      
      onClose();
    } catch (error) {
      console.error('Error enrolling students:', error);
      toast.error('Failed to enroll students');
    } finally {
      setEnrolling(false);
    }
  };

  const toggleStudentSelection = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const filteredStudents = students.filter(student => {
    const matchesSearch = 
      student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.phone?.includes(searchTerm);
    
    return matchesSearch;
  });

  const availableStudents = filteredStudents.filter(
    student => !enrolledStudents.includes(student.email?.toLowerCase())
  );

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-8">
          <Loader className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading students...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              <UserPlus className="w-6 h-6 mr-2" />
              Enroll Students in Batch
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Select students from your student management to enroll
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search students by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>
          
          {selectedStudents.length > 0 && (
            <div className="mt-2 text-sm text-blue-600 dark:text-blue-400">
              {selectedStudents.length} student(s) selected
            </div>
          )}
        </div>

        {/* Students List */}
        <div className="flex-1 overflow-y-auto p-4">
          {availableStudents.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">
                {students.length === 0 
                  ? 'No students found in student management. Please add students first.'
                  : searchTerm
                  ? 'No students found matching your search'
                  : 'All students are already enrolled in this batch'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {availableStudents.map((student) => {
                const isSelected = selectedStudents.includes(student.id);
                
                return (
                  <div
                    key={student.id}
                    onClick={() => toggleStudentSelection(student.id)}
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            isSelected
                              ? 'border-blue-500 bg-blue-500'
                              : 'border-gray-300 dark:border-gray-600'
                          }`}>
                            {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900 dark:text-white">
                              {student.name}
                            </h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {student.email}
                            </p>
                          </div>
                        </div>
                        {(student.fatherName || student.motherName || student.phone) && (
                          <div className="mt-2 ml-8 text-sm text-gray-500 dark:text-gray-400">
                            {student.fatherName && <span>Father: {student.fatherName}</span>}
                            {student.motherName && <span className="ml-4">Mother: {student.motherName}</span>}
                            {student.phone && <span className="ml-4">Phone: {student.phone}</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <AlertCircle className="w-4 h-4 inline mr-1" />
              Only students from Student Management can be enrolled
            </div>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                disabled={enrolling}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleEnrollStudents}
                disabled={enrolling || selectedStudents.length === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {enrolling ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    <span>Enrolling...</span>
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4" />
                    <span>Enroll Selected ({selectedStudents.length})</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BatchStudentEnrollment;
